// Notebook.js
import { useState, useRef, useEffect, useCallback } from "react";
import { addStyles } from "react-mathquill";
import { customParser1 } from "./customparser1";
import { customParser2 } from "./customparser2";
import useSpeechRecognition from "./useSpeechRecognition";
import useMicPermission from "./useMicPermission";
import { NotebookTextBlock, NotebookMathBlock } from "./Utility/Textblocks";

addStyles();

export default function Notebook({ onSubmit, timerSeconds = null, preview = false }) {

  // ── Blocks state ──
  // Each block: { type: "text", content: "" } or { type: "math", content: [""] }
  const [blocks, setBlocks] = useState([
    { type: "text", content: "" },
    { type: "math", content: [""] },
  ]);

  // ── Focus tracking ──
  // { index: blockIndex, type: "text" | "math" }

  // Default to text block (index 0)
  const focusedRef = useRef({ index: 0, type: "text" });



  // ── Recording ──
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const { permissionState, checkAndRequest } = useMicPermission();

  // ── Timer ──
  const [timeLeft, setTimeLeft] = useState(timerSeconds);
  const timeUpRef = useRef(false);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  // Timer countdown — pure, no side effects
  useEffect(() => {
    if (timerSeconds === null) return;
    setTimeLeft(timerSeconds);
    timeUpRef.current = false;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          timeUpRef.current = true;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerSeconds]);

  // Detect time up — fires once
  useEffect(() => {
    if (timeLeft === 0 && timeUpRef.current) {
      timeUpRef.current = false; // prevent double fire
      onSubmit?.(blocksRef.current);
    }
  }, [timeLeft]);

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const timerUrgent = timeLeft !== null && timeLeft <= 10;

  // ── Speech handlers ──
  const recordingRef = useRef(false);
  recordingRef.current = recording;

  const handleInterim = useCallback((text) => {
    if (!text) {
      setInterim("");
      return;
    }
    const focused = focusedRef.current;
    if (focused.type === "math") {
      setInterim(customParser2(text));
    } else {
      setInterim(customParser1(text));
    }
  }, []);

  const handleFinal = useCallback((text) => {
    if (!text) return;
    const focused = focusedRef.current;
    const blockIndex = focused.index;

    if (blockIndex === null) return;

    setBlocks((prev) => {
      const next = [...prev];
      const block = { ...next[blockIndex] };

      if (block.type === "math") {
        const parsed = customParser2(text);
        // Append to the last line of the math block
        const lines = [...block.content];
        const lastIdx = lines.length - 1;
        lines[lastIdx] = (lines[lastIdx] + " " + parsed).trim();
        block.content = lines;
      } else {
        const parsed = customParser1(text);
        block.content = (block.content + " " + parsed).trim();
      }

      next[blockIndex] = block;
      return next;
    });
    setInterim("");
  }, []);

  const { isListening, start, stop } = useSpeechRecognition({
    onInterim: handleInterim,
    onFinal: handleFinal,
  });

  const toggleRecording = useCallback(async () => {
    if (recording) {
      stop();
      setRecording(false);
      setInterim("");
    } else {
      const allowed = await checkAndRequest();
      if (!allowed) {
        alert(
          permissionState === "denied"
            ? "Microphone access was blocked. Please allow mic access in your browser settings and reload the page."
            : "Microphone not available. Please check your device."
        );
        return;
      }
      if (isListening) stop();
      setRecording(true);
      setInterim("");
      setTimeout(() => start(), 50);
    }
  }, [recording, isListening, start, stop, checkAndRequest, permissionState]);

  // ── Block focus handler ──
  // The handler stays the same — it only SETS, never clears
  const handleBlockFocus = useCallback((index, type) => {
    focusedRef.current = { index, type };
  }, []);

  // ── Block content change (keyboard edits) ──
  const handleBlockChange = useCallback((index, newContent) => {
    setBlocks((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], content: newContent };
      return next;
    });
  }, []);

  // ── Get interim for a specific block ──
  const getInterim = (index) => {
    if (!recording || !interim) return "";
    if (focusedRef.current.index === index) return interim;
    return "";
  };

  return (
    <div style={{ width: "100%", height: "100%", overflow: "auto", background: "#fff" }}>
      <style>{`
        .mq-editable-field.mq-focused {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .mq-editable-field {
          white-space: nowrap !important;
          overflow: visible !important;
        }
        .mq-editable-field .mq-root-block {
          font-family: 'Georgia', 'Times New Roman', serif !important;
          font-style: normal !important;
          font-size: 20px !important;
          color: #1a1a1a !important;
          letter-spacing: 0.01em !important;
        }
        .mq-math-mode var {
          font-family: 'Georgia', 'Times New Roman', serif !important;
          font-style: normal !important;
        }
      `}</style>

      {/* ── Blocks ── */}
      <div style={{ padding: "16px 16px 100px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {blocks.map((block, index) =>
          block.type === "text" ? (
            <NotebookTextBlock
              key={`text-${index}`}
              content={block.content}
              interim={getInterim(index)}
              onChange={(val) => handleBlockChange(index, val)}
              onFocus={() => handleBlockFocus(index, "text")}
            />
          ) : (
            <NotebookMathBlock
              key={`math-${index}`}
              content={block.content}
              interim={getInterim(index)}
              onChange={(val) => handleBlockChange(index, val)}
              onFocus={() => handleBlockFocus(index, "math")}
            />
          )
        )}
      </div>

      {/* ── Bottom Toolbar ── */}
      {!preview && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#FFFFFF",
            borderRadius: 40,
            padding: "8px 8px 8px 16px",
            boxShadow: "0 4px 32px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)",
            border: "2px solid #FDE68A",
            zIndex: 100,
          }}
        >
          {/* Timer */}
          {timerSeconds !== null && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: timerUrgent ? "#FEE2E2" : "#FEF3C7",
                borderRadius: 30,
                padding: "6px 14px",
                marginRight: 4,
                border: `1.5px solid ${timerUrgent ? "#FCA5A5" : "#FDE68A"}`,
                transition: "background 0.3s, border 0.3s",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke={timerUrgent ? "#EF4444" : "#F59E0B"} strokeWidth="2" />
                <path d="M12 6v6l4 2" stroke={timerUrgent ? "#EF4444" : "#F59E0B"} strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: 800,
                  fontSize: 15,
                  color: timerUrgent ? "#DC2626" : "#92400E",
                  letterSpacing: "0.04em",
                  minWidth: 38,
                  textAlign: "center",
                }}
              >
                {formatTime(timeLeft)}
              </span>
            </div>
          )}

          {/* Single Mic button */}
          <button
            onClick={toggleRecording}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              background: recording ? "#FEF3C7" : "transparent",
              border: recording ? "1.5px solid #FDE68A" : "1.5px solid transparent",
              cursor: "pointer",
              padding: "8px 18px",
              borderRadius: 30,
              color: recording ? "#D97706" : "#9CA3AF",
              transition: "all 0.2s",
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {recording ? (
                <rect x="4" y="4" width="16" height="16" rx="3" fill="currentColor" stroke="none" />
              ) : (
                <>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </>
              )}
            </svg>
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                fontFamily: "'Nunito', sans-serif",
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              Speak
            </span>
          </button>

          {/* Divider */}
          <div style={{ width: 1.5, height: 36, background: "#F0E8C8", borderRadius: 2, margin: "0 4px" }} />

          {/* Submit */}
          <button
            onClick={() => onSubmit?.(blocks)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              background: "linear-gradient(135deg, #FBBF24, #F59E0B)",
              color: "#fff",
              border: "none",
              borderRadius: 30,
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 800,
              fontFamily: "'Nunito', sans-serif",
              cursor: "pointer",
              letterSpacing: "0.02em",
              boxShadow: "0 3px 12px rgba(245,158,11,0.35)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.96)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Submit
          </button>
        </div>
      )}
    </div>
  );
}